import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import axios from "axios";
import { API } from "../App";
import { Home, Wrench, Zap, Wind, Trees, Sparkles, PaintBucket, HardHat, Menu, X, ChevronRight, Star, Shield, Clock, Users, Hammer } from "lucide-react";
import { Button } from "../components/ui/button";

const categoryIcons = {
  plumbing: Wrench,
  electrical: Zap,
  hvac: Wind,
  landscaping: Trees,
  cleaning: Sparkles,
  roofing: HardHat,
  painting: PaintBucket,
  handyman: Hammer
};

export default function LandingPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      /* silent - error deliberately ignored */
    } finally {
      setLoading(false);
    }
  };

  const handleGetStarted = () => {
    navigate('/request');
  };

  const handleDashboardClick = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // Navigate to the appropriate dashboard based on user role
    if (user.role === 'contractor') {
      navigate('/contractor');
    } else if (user.role === 'admin') {
      navigate('/admin');
    } else {
      // Default to homeowner dashboard
      navigate('/dashboard');
    }
  };

  const handleCategoryClick = (category) => {
    navigate(`/request/${category.slug}`);
  };

  const getCategoryIcon = (slug) => {
    const IconComponent = categoryIcons[slug] || Wrench;
    return <IconComponent className="w-6 h-6" />;
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo">
              <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
                <Home className="w-5 h-5 text-white" />
              </div>
              <span className="font-manrope font-bold text-xl text-[#1F2933]">Contractor Hub LLC</span>
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#services" className="nav-link">Services</a>
              <a href="#how-it-works" className="nav-link">How It Works</a>
              <a href="#why-us" className="nav-link">Why Us</a>
              <Link to="/property-managers" className="nav-link">Property Managers</Link>
            </div>

            {/* Auth Buttons */}
            <div className="hidden md:flex items-center gap-4">
              {user ? (
                <>
                  <Button variant="ghost" onClick={handleDashboardClick} data-testid="dashboard-btn">
                    Dashboard
                  </Button>
                  <Button 
                    onClick={logout}
                    className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white rounded-full px-6"
                    data-testid="logout-btn"
                  >
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" data-testid="login-btn">Log In</Button>
                  </Link>
                  <Link to="/register">
                    <Button 
                      className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white rounded-full px-6"
                      data-testid="register-btn"
                    >
                      Get Started
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-100 bg-white py-4 px-6">
            <div className="flex flex-col gap-4">
              <a href="#services" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>Services</a>
              <a href="#how-it-works" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
              <a href="#why-us" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>Why Us</a>
              <Link to="/property-managers" className="nav-link py-2" onClick={() => setMobileMenuOpen(false)}>Property Managers</Link>
              <hr className="border-gray-100" />
              {user ? (
                <>
                  <Button variant="ghost" onClick={() => { handleDashboardClick(); setMobileMenuOpen(false); }} className="justify-start">Dashboard</Button>
                  <Button onClick={logout} className="bg-[#0FA3B1] text-white">Logout</Button>
                </>
              ) : (
                <>
                  <Link to="/login"><Button variant="ghost" className="w-full justify-start">Log In</Button></Link>
                  <Link to="/register"><Button className="w-full bg-[#0FA3B1] text-white">Get Started</Button></Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-8 hero-gradient" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#0FA3B1]/10 rounded-full text-[#0FA3B1] text-sm font-medium mb-6">
                <Star className="w-4 h-4" /> Trusted by Austin Homeowners
              </span>
              <h1 className="font-manrope font-bold text-4xl sm:text-5xl lg:text-6xl text-[#1F2933] leading-tight mb-6">
                Find Trusted Home Service Pros in{" "}
                <span className="text-[#0FA3B1]">Austin, TX</span>
              </h1>
              <p className="text-lg text-[#1F2933]/70 mb-8 max-w-lg">
                Connect with local contractors for painting, flooring, remodeling, and more.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  onClick={handleGetStarted}
                  className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white rounded-full px-8 py-6 text-lg btn-shine"
                  data-testid="hero-cta-btn"
                >
                  Get Free Quotes <ChevronRight className="w-5 h-5 ml-2" />
                </Button>
              </div>
              <div className="mt-4 flex flex-col items-start gap-2">
                <span className="text-[#1F2933]/70 text-sm">
                  Are you a contractor?
                </span>
                <Button
                  onClick={() => navigate('/register?role=contractor')}
                  className="bg-[#1F2933] hover:bg-[#1F2933]/90 text-white px-6 py-2 rounded-lg"
                  data-testid="contractor-cta-btn"
                >
                  Join Our Network
                </Button>
              </div>
            </div>
            
            <div className="relative animate-slide-up stagger-2">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img 
                  src="https://images.unsplash.com/photo-1760386129099-33a45c911394?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwyfHxBdXN0aW4lMjBUZXhhcyUyMHNreWxpbmUlMjBtb2Rlcm58ZW58MHx8fHwxNzY4NTk3NzM0fDA&ixlib=rb-4.1.0&q=85"
                  alt="Austin Texas"
                  className="w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1F2933]/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="glass rounded-2xl p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-[#7ED957] flex items-center justify-center">
                      <Users className="w-6 h-6 text-[#1F2933]" />
                    </div>
                    <div>
                      <p className="font-semibold text-[#1F2933]">Find top rated pros in your area</p>
                      <p className="text-sm text-[#1F2933]/70">Ready to help you today</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-6 lg:px-8 bg-gray-50" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-manrope font-bold text-3xl md:text-4xl text-[#1F2933] mb-4">
              Popular Services
            </h2>
            <p className="text-[#1F2933]/70 max-w-2xl mx-auto">
              Browse our most requested home services and connect with top-rated professionals
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {categories.map((category, index) => (
                <button 
                  key={category.category_id}
                  onClick={() => handleCategoryClick(category)}
                  className={`category-card card-hover animate-fade-in stagger-${Math.min(index + 1, 8)} text-left`}
                  data-testid={`category-card-${category.slug}`}
                >
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src={category.image_url} 
                      alt={category.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="category-overlay"></div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-[#0FA3B1] flex items-center justify-center text-white">
                        {getCategoryIcon(category.slug)}
                      </div>
                      <div>
                        <h3 className="font-manrope font-semibold text-white">{category.name}</h3>
                        <p className="text-white/70 text-sm">Get quotes now</p>
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-6 lg:px-8" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-manrope font-bold text-3xl md:text-4xl text-[#1F2933] mb-4">
              How It Works
            </h2>
            <p className="text-[#1F2933]/70 max-w-2xl mx-auto">
              Get connected with qualified contractors in three easy steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                step: "01", 
                title: "Tell Us What You Need", 
                desc: "Select a service category and describe your project. It takes less than 2 minutes.",
                icon: <Home className="w-6 h-6" />
              },
              { 
                step: "02", 
                title: "Choose How Many Quotes", 
                desc: "Select 1-3 contractors to contact you. More quotes means more options.",
                icon: <Users className="w-6 h-6" />
              },
              { 
                step: "03", 
                title: "Get Contacted by Pros", 
                desc: "Qualified contractors will reach out to discuss your project and provide quotes.",
                icon: <Wrench className="w-6 h-6" />
              }
            ].map((item, index) => (
              <div 
                key={item.step}
                className={`relative p-8 rounded-2xl bg-white border border-gray-100 shadow-sm animate-slide-up stagger-${index + 1}`}
              >
                <div className="absolute -top-4 -left-4 w-12 h-12 rounded-full bg-[#0FA3B1] text-white flex items-center justify-center font-manrope font-bold text-lg">
                  {item.step}
                </div>
                <div className="w-14 h-14 rounded-xl bg-[#0FA3B1]/10 flex items-center justify-center text-[#0FA3B1] mb-6 mt-4">
                  {item.icon}
                </div>
                <h3 className="font-manrope font-semibold text-xl text-[#1F2933] mb-3">{item.title}</h3>
                <p className="text-[#1F2933]/70">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section id="why-us" className="py-20 px-6 lg:px-8 bg-[#1F2933]" data-testid="why-us-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-manrope font-bold text-3xl md:text-4xl text-white mb-4">
              Why Choose Contractor Hub LLC
            </h2>
            <p className="text-white/70 max-w-2xl mx-auto">
              We make finding reliable home service professionals simple and stress-free
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                icon: <Shield className="w-8 h-8" />,
                title: "Top-Rated Local Contractors",
                desc: "Connect with highly-reviewed pros trusted by Austin homeowners"
              },
              { 
                icon: <Clock className="w-8 h-8" />,
                title: "Quick Response",
                desc: "Get contacted by qualified pros within 24 hours of your request"
              },
              { 
                icon: <Star className="w-8 h-8" />,
                title: "Local Experts",
                desc: "Connect with Austin-based professionals who know the area"
              }
            ].map((item, index) => (
              <div 
                key={item.title}
                className={`p-8 rounded-2xl bg-white/5 border border-white/10 animate-fade-in stagger-${index + 1}`}
              >
                <div className="w-16 h-16 rounded-xl bg-[#0FA3B1] flex items-center justify-center text-white mb-6">
                  {item.icon}
                </div>
                <h3 className="font-manrope font-semibold text-xl text-white mb-3">{item.title}</h3>
                <p className="text-white/70">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 lg:px-8" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-manrope font-bold text-3xl md:text-4xl text-[#1F2933] mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-[#1F2933]/70 text-lg mb-8 max-w-2xl mx-auto">
            Join thousands of Austin homeowners who trust Contractor Hub LLC for their home service needs.
          </p>
          <Button 
            onClick={handleGetStarted}
            className="bg-[#0FA3B1] hover:bg-[#0D8C98] text-white rounded-full px-10 py-6 text-lg btn-shine"
            data-testid="final-cta-btn"
          >
            Get Your Free Quote <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer" data-testid="footer">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[#0FA3B1] flex items-center justify-center">
                  <Home className="w-5 h-5 text-white" />
                </div>
                <span className="font-manrope font-bold text-xl text-white">Contractor Hub LLC</span>
              </div>
              <p className="text-white/60 text-sm">
                Connecting Austin homeowners with trusted local service professionals since 2024.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Services</h4>
              <ul className="space-y-2">
                {categories.map(cat => (
                  <li key={cat.category_id}>
                    <Link to={`/request/${cat.slug}`} className="footer-link text-sm">
                      {cat.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">For Contractors</h4>
              <ul className="space-y-2">
                <li><Link to="/register?role=contractor" className="footer-link text-sm">Join Our Network</Link></li>
                <li><Link to="/login" className="footer-link text-sm">Contractor Login</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-white/60">
                <li>Austin, Texas</li>
                <li>support@contractorhub.com</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-8 text-center">
            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} Contractor Hub LLC. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
